import { useState, useEffect, useRef, useCallback } from 'react';
import { calculateVisibleLabelsCount } from '@util/helpers';

export const useLabelsBar = (labels) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(labels.length);
  const containerRef = useRef();
  const resizeObserverRef = useRef();

  const calculateVisibleCount = useCallback(() => {
    return calculateVisibleLabelsCount({
      labels,
      containerRef
    });
  }, [labels.length, containerRef]);

  useEffect(() => {
    if (isExpanded) {
      setVisibleCount(labels.length);
      return;
    }

    if (labels.length === 0) {
      setVisibleCount(0);
      return;
    }

    const timeoutId = setTimeout(() => {
      const newCount = calculateVisibleCount();
      setVisibleCount(newCount);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [labels.length, isExpanded, calculateVisibleCount]);

  const handleResize = useCallback(() => {
    if (isExpanded) return;

    const newCount = calculateVisibleCount();
    setVisibleCount(newCount);
  }, [calculateVisibleCount, isExpanded]);

  useEffect(() => {
    if (!containerRef.current || isExpanded) {
      return;
    }

    resizeObserverRef.current = new ResizeObserver(handleResize);
    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [handleResize, isExpanded]);

  const hiddenCount = labels.length - visibleCount;

  return { containerRef, hiddenCount, isExpanded, setIsExpanded };
};
